import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import swal from "sweetalert";
import { addCompany } from "state/company/actions";
import { initialize, addAddress } from "state/adminData/actions";
import AddressAutocompleteInput from "./AddressAutocompleteInput";
import {
  setCompany,
} from "state/orderEdition/actions";
import { ATSCompany } from "transformer/company/companyTransformer";

const AddCompanyComponent = ({ onCreated = null }) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const customers = useSelector((state) => state.adminData.customers || []);
  const addresses = useSelector((state) => state.adminData.addresses || []);
  const companies = useSelector((state) => state.adminData.companies || []);
  const formatAddressPreview = useCallback((address) => {
    if (!address) {
      return "";
    }
    return [
      address.street_number,
      address.street,
      address.postal_code,
      address.city,
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const buildAddressOption = useCallback(
    (address) => {
      const preview = formatAddressPreview(address);
      return {
        value: address.id,
        label: [address.name_address, preview].filter(Boolean).join(" - "),
        preview,
        ...address,
      };
    },
    [formatAddressPreview]
  );

  const filterAddressOption = (option, rawInput) => {
    if (!rawInput) {
      return true;
    }
    const input = rawInput.toLowerCase();
    const data = option.data || option;
    return [
      option.label,
      data.name_address,
      data.preview,
      data.street,
      data.street_number,
      data.postal_code,
      data.city,
    ]
      .filter(Boolean)
      .some((value) => value.toString().toLowerCase().includes(input));
  };

  const formatAddressOptionLabel = (option) => (
    <div>
      <div>{option.name_address || option.label}</div>
      <small className="text-muted">
        {option.preview || formatAddressPreview(option) || "Adresse inconnue"}
      </small>
    </div>
  );

  const addressesFormat = useMemo(
    () => addresses.map(buildAddressOption),
    [addresses, buildAddressOption]
  );

  const customersFormat = customers.map((customer) => ({
    value: customer.id,
    label: customer.email,
    ...customer,
  }));

  const companiesFormat = companies.map((company) => ({
    value: company.id,
    label: company.name,
    ...company,
  }));

  const [companyInfo, setCompanyInfo] = useState({
    name: "",
    billOnChorus: false,
    specificBilling: false,
    domain: "",
    siret: "",
    email: "",
    phone: "",
    customers: [],
    billingAddress: "",
    deliveryAddresses: [],
    domains: [],
    parent: null,
  });

  // Création d'une adresse de facturation à la volée (carnet vide pour une nouvelle entreprise)
  const [showNewBillingAddress, setShowNewBillingAddress] = useState(false);
  const [newBillingAddress, setNewBillingAddress] = useState(null);
  const [newBillingAddressName, setNewBillingAddressName] = useState("");
  const [newBillingAddressComplement, setNewBillingAddressComplement] = useState("");
  const [useAsDeliveryAddress, setUseAsDeliveryAddress] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCompanyInfo({
      ...companyInfo,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSelectChange = (selectedOptions, { name }) => {
    setCompanyInfo({
      ...companyInfo,
      [name]: selectedOptions,
    });
  };

  // Ajoutez cette fonction pour gérer les changements dans le tableau de domaines
  const handleDomainsChange = (index, value) => {
    const newDomains = [...companyInfo.domains];
    newDomains[index] = value;
    setCompanyInfo({
      ...companyInfo,
      domains: newDomains,
    });
  };

  // Ajoutez cette fonction pour ajouter un nouveau champ de domaine
  const addDomainField = () => {
    setCompanyInfo({
      ...companyInfo,
      domains: [...companyInfo.domains, ""],
    });
  };

  // Ajoutez cette fonction pour supprimer un champ de domaine
  const removeDomainField = (index) => {
    const newDomains = companyInfo.domains.filter((_, i) => i !== index);
    setCompanyInfo({
      ...companyInfo,
      domains: newDomains,
    });
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Indispensable quand la modale est ouverte depuis la modale "Ajouter un client" :
    // les événements React traversent les portals, sans ça le submit remonte au
    // formulaire client parent qui se soumet à vide (POST /api/pro/customers en 400).
    e.stopPropagation();
    try {
      setIsLoading(true);

      let billingAddressId = companyInfo?.billingAddress?.id;
      const deliveryAddressIds = companyInfo.deliveryAddresses.map((address) => address.id);

      // Création de la nouvelle adresse de facturation avant l'entreprise
      if (showNewBillingAddress) {
        if (!newBillingAddress) {
          setIsLoading(false);
          swal({
            title: "Attention ",
            text: "Sélectionnez une adresse dans la recherche pour créer l'adresse de facturation",
            icon: "warning",
            dangerMode: true,
          });
          return;
        }
        const addressResp = await dispatch(
          addAddress({
            name_address: newBillingAddressName || companyInfo.name,
            street_number: newBillingAddress.streetNumber,
            street: newBillingAddress.street,
            postal_code: newBillingAddress.postalCode,
            city: newBillingAddress.city,
            address_complement: newBillingAddressComplement,
            delivery_information: "",
            recover_information: "",
            parking_information: "",
            useful_number: "",
            note: "",
          })
        );
        // L'intercepteur axios renvoie directement le body : l'adresse créée est addressResp lui-même
        const createdAddress = addressResp?.id ? addressResp : addressResp?.result;
        if (!createdAddress?.id || createdAddress?.success === false) {
          setIsLoading(false);
          swal({
            title: "Attention ",
            text: createdAddress?.message || "La création de l'adresse de facturation a échoué",
            icon: "warning",
            dangerMode: true,
          });
          return;
        }
        billingAddressId = createdAddress.id;
        if (useAsDeliveryAddress) {
          deliveryAddressIds.push(createdAddress.id);
        }
      }

      const companyData = {
        ...companyInfo,
        customers: companyInfo.customers.map((customer) => customer.value),
        domains: companyInfo.domains.map((domain) => {
          return {domain: domain};
        }),
        parent: companyInfo?.parent?.id,
        billingAddress: billingAddressId,
        deliveryAddresses: deliveryAddressIds,
      };
      await dispatch(addCompany(companyData)).then(async (resp) => {
        setIsLoading(false);
        let msg = "L'entreprise a été ajoutée avec succès";
        if (resp?.result?.success === false || resp?.success === false) {
          msg = resp?.result?.message || resp?.message || "Une erreur est survenue";
          swal({
            title: "Attention ",
            text: msg,
            icon: "warning",
            dangerMode: true,
          });
        } else {
          // Si ok, récupère le dernier client dans le listing
          const company = ATSCompany(companyData);
          await dispatch(setCompany(company));
          // Refresh silencieux quand on est appelé depuis la modale "Ajouter un
          // client" : un initialize bloquant afficherait le Loader plein écran,
          // ce qui démonte la sidebar et ferme la modale client en cours de saisie
          dispatch(initialize(!!onCreated));
          // Callback optionnel (ex. modale Ajouter un client) avec l'entreprise
          // créée — la réponse API contient l'id, fallback sur les données saisies
          if (onCreated) {
            onCreated(resp?.company || company);
          }
          handleClose();
          swal({
            title: "Information",
            text: msg,
            icon: "success",
            dangerMode: false,
          });
        }
      });
    } catch (err) {
      setIsLoading(false);
      swal({
        title: "Attention ",
        text: err?.message || "Une erreur est survenue",
        icon: "warning",
        dangerMode: true,
      });
    }
  };

  return (
    <>
      <Button variant="success" onClick={handleShow} style={{zIndex: '1'}}>
        +
      </Button>

      <Modal show={show} onHide={handleClose} dialogClassName="modal-xl">
        <Modal.Header closeButton>
          <Modal.Title>Ajouter une entreprise</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <div>Enregistrement en cours..</div>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Nom de l'entreprise*</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={companyInfo.name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>A facturer sur chorus</Form.Label>
                    <Form.Check
                      type="checkbox"
                      name="billOnChorus"
                      checked={companyInfo.billOnChorus}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Facturation spécifique</Form.Label>
                    <Form.Check
                      type="checkbox"
                      name="specificBilling"
                      checked={companyInfo.specificBilling}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Nom de domaine PRINCIPAL de l'entreprise (sans @)*</Form.Label>
                    <Form.Control
                      type="text"
                      name="domain"
                      value={companyInfo.domain}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>SIRET</Form.Label>
                    <Form.Control
                      type="text"
                      name="siret"
                      value={companyInfo.siret}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Email (pour la facturation)</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={companyInfo.email}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Téléphone de l'entreprise</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone"
                      value={companyInfo.phone}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Clients</Form.Label>
                    <Select
                      options={customersFormat}
                      isMulti
                      name="customers"
                      value={companyInfo.customers}
                      onChange={handleSelectChange}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Adresse de facturation</Form.Label>
                    <Select
                      options={addressesFormat}
                      name="billingAddress"
                      value={companyInfo.billingAddress}
                      onChange={handleSelectChange}
                      formatOptionLabel={formatAddressOptionLabel}
                      filterOption={filterAddressOption}
                      isDisabled={showNewBillingAddress}
                      isClearable
                    />
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 mt-1"
                      onClick={() => setShowNewBillingAddress(!showNewBillingAddress)}
                    >
                      {showNewBillingAddress
                        ? "Annuler la nouvelle adresse"
                        : "+ Créer une nouvelle adresse de facturation"}
                    </Button>
                    {showNewBillingAddress && (
                      <div className="border rounded p-2 mt-2">
                        <Form.Group>
                          <Form.Label>Nom de l'adresse</Form.Label>
                          <Form.Control
                            type="text"
                            value={newBillingAddressName}
                            onChange={(e) => setNewBillingAddressName(e.target.value)}
                            placeholder="Par défaut : nom de l'entreprise"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Adresse*</Form.Label>
                          <AddressAutocompleteInput onSelect={setNewBillingAddress} />
                          {newBillingAddress && (
                            <small className="text-muted">
                              {[
                                newBillingAddress.streetNumber,
                                newBillingAddress.street,
                                newBillingAddress.postalCode,
                                newBillingAddress.city,
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            </small>
                          )}
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Complément d'adresse</Form.Label>
                          <Form.Control
                            type="text"
                            value={newBillingAddressComplement}
                            onChange={(e) => setNewBillingAddressComplement(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Check
                          type="checkbox"
                          id="useAsDeliveryAddress"
                          label="Utiliser aussi comme adresse de livraison"
                          checked={useAsDeliveryAddress}
                          onChange={(e) => setUseAsDeliveryAddress(e.target.checked)}
                        />
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Adresses de livraison</Form.Label>
                    <Select
                      options={addressesFormat}
                      isMulti
                      name="deliveryAddresses"
                      value={companyInfo.deliveryAddresses}
                      onChange={handleSelectChange}
                      formatOptionLabel={formatAddressOptionLabel}
                      filterOption={filterAddressOption}
                    />
                  </Form.Group>
                  <Form.Group>
                  <Form.Label>Autres noms de domaine</Form.Label>
                  {companyInfo.domains.map((domain, index) => (
                    <div key={index} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        value={domain}
                        onChange={(e) => handleDomainsChange(index, e.target.value)}
                      />
                      <Button
                        variant="danger"
                        onClick={() => removeDomainField(index)}
                        className="ml-2"
                      >
                        Supprimer
                      </Button>
                    </div>
                  ))}
                  <Button variant="secondary" onClick={addDomainField}>
                    Ajouter un domaine
                  </Button>
                </Form.Group>
                  <Form.Group>
                    <Form.Label>Société mère</Form.Label>
                    <Select
                      options={companiesFormat}
                      name="parent"
                      value={companyInfo.parent}
                      onChange={handleSelectChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="success" type="submit" block size="lg" className="mt-3">
                Enregistrer
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddCompanyComponent;